import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Clock, Share2, Twitter, Linkedin, Copy } from 'lucide-react';
import PageHero from '../components/PageHero';
import posts from '../data/blog/posts.json';

const BlogIndex = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  const categories = ['All', 'Product', 'Engineering', 'AI', 'Announcements'];

  const filteredPosts = activeFilter === 'All' 
    ? posts 
    : posts.filter(post => post.category === activeFilter);

  return (
    <div className="relative">
      <PageHero 
        title="Updates"
        subtitle="Latest news, insights, and announcements from the ROMIND team."
      />

      <section className="section-padding pt-0">
        <div className="max-w-7xl mx-auto">
          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-12" data-testid="blog-filters">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveFilter(category)}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  activeFilter === category
                    ? 'bg-violet-500 text-white'
                    : 'bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white border border-white/10'
                }`}
                data-testid={`filter-${category.toLowerCase()}`}
              >
                {category}
              </button>
            ))}
          </div>

          {/* Posts Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPosts.map((post) => (
              <Link
                key={post.id}
                to={`/updates/${post.slug}`}
                className="glass-panel p-6 group hover:border-violet-500/30 transition-all duration-300"
                data-testid={`blog-post-card-${post.slug}`}
              >
                {post.featured && (
                  <span className="inline-block px-3 py-1 text-xs font-medium bg-violet-500/20 text-violet-400 rounded-full mb-4">
                    Featured
                  </span>
                )}
                <span className="text-sm text-violet-400 mb-2 block">{post.category}</span>
                <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-violet-300 transition-colors">
                  {post.title}
                </h3>
                <p className="text-slate-400 text-sm mb-4 line-clamp-3">
                  {post.excerpt}
                </p>
                <div className="flex items-center gap-4 text-xs text-slate-500">
                  <span>{post.publishedAt}</span>
                  <span className="flex items-center gap-1">
                    <Clock size={12} />
                    {post.readingTime}
                  </span>
                </div>
              </Link>
            ))}
          </div>

          {filteredPosts.length === 0 && (
            <div className="text-center py-16">
              <p className="text-slate-400">No posts found in this category.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

const BlogPost = () => {
  const { slug } = useParams();
  const post = posts.find(p => p.slug === slug);

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Post not found</h1>
          <Link to="/updates" className="text-violet-400 hover:text-violet-300">
            ← Back to Updates
          </Link>
        </div>
      </div>
    );
  }

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    alert('Link copied to clipboard!');
  };

  // Simple markdown to HTML conversion
  const renderContent = (content) => {
    const lines = content.split('\n');
    return lines.map((line, index) => {
      if (line.startsWith('# ')) {
        return <h1 key={index} className="text-3xl md:text-4xl font-bold text-white mt-8 mb-4">{line.slice(2)}</h1>;
      }
      if (line.startsWith('## ')) {
        return <h2 key={index} className="text-2xl md:text-3xl font-semibold text-white mt-8 mb-4">{line.slice(3)}</h2>;
      }
      if (line.startsWith('### ')) {
        return <h3 key={index} className="text-xl font-semibold text-white mt-6 mb-3">{line.slice(4)}</h3>;
      }
      if (line.startsWith('- **')) {
        const match = line.match(/- \*\*(.+?)\*\*: (.+)/);
        if (match) {
          return (
            <li key={index} className="text-slate-400 mb-2">
              <strong className="text-white">{match[1]}</strong>: {match[2]}
            </li>
          );
        }
      }
      if (line.startsWith('- ')) {
        return <li key={index} className="text-slate-400 mb-2 ml-4">{line.slice(2)}</li>;
      }
      if (line.startsWith('*') && line.endsWith('*')) {
        return <p key={index} className="text-slate-500 italic my-4">{line.slice(1, -1)}</p>;
      }
      if (line.startsWith('---')) {
        return <hr key={index} className="border-white/10 my-8" />;
      }
      if (line.trim() === '') {
        return <br key={index} />;
      }
      return <p key={index} className="text-slate-400 leading-relaxed mb-4">{line}</p>;
    });
  };

  return (
    <div className="relative">
      <section className="pt-32 pb-8 section-padding">
        <div className="max-w-3xl mx-auto">
          <Link 
            to="/updates" 
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-8"
            data-testid="back-to-updates"
          >
            <ArrowLeft size={18} />
            Back to Updates
          </Link>

          <span className="text-sm text-violet-400 mb-4 block">{post.category}</span>
          
          <h1 
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6"
            data-testid="blog-post-title"
          >
            {post.title}
          </h1>

          <div className="flex flex-wrap items-center gap-6 text-sm text-slate-500 mb-8">
            <span>{post.author}</span>
            <span>{post.publishedAt}</span>
            <span className="flex items-center gap-1">
              <Clock size={14} />
              {post.readingTime}
            </span>
          </div>

          {/* Share buttons */}
          <div className="flex items-center gap-3 mb-12" data-testid="share-buttons">
            <span className="text-sm text-slate-500">Share:</span>
            <button 
              onClick={copyLink}
              className="p-2 rounded-lg bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white transition-colors"
              aria-label="Copy link"
            >
              <Copy size={18} />
            </button>
            <a 
              href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(post.title)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-lg bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white transition-colors"
              aria-label="Share on Twitter"
            >
              <Twitter size={18} />
            </a>
            <a 
              href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(window.location.href)}&title=${encodeURIComponent(post.title)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 rounded-lg bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white transition-colors"
              aria-label="Share on LinkedIn"
            >
              <Linkedin size={18} />
            </a>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding pt-0">
        <div className="max-w-3xl mx-auto">
          <article className="prose prose-invert prose-lg max-w-none" data-testid="blog-post-content">
            {renderContent(post.content)}
          </article>

          {/* Tags */}
          <div className="mt-12 pt-8 border-t border-white/10">
            <div className="flex flex-wrap gap-2">
              {post.tags.map((tag) => (
                <span 
                  key={tag}
                  className="px-3 py-1 text-xs bg-white/5 text-slate-400 rounded-full"
                >
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding cosmic-bg">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Stay updated with ROMIND
          </h2>
          <p className="text-slate-400 mb-8">
            Join our early access list for the latest updates and exclusive insights.
          </p>
          <a 
            href="/#early-access" 
            className="btn-primary"
            data-testid="blog-cta"
          >
            Get Early Access
          </a>
        </div>
      </section>
    </div>
  );
};

const Updates = () => {
  const { slug } = useParams();
  
  if (slug) {
    return <BlogPost />;
  }
  
  return <BlogIndex />;
};

export default Updates;
